import React, { useState, useEffect } from 'react';
import { Plus, X, Edit3, Save, Building, Car, Wrench, Package, DollarSign, Loader2 } from 'lucide-react';

interface Asset {
  id: string;
  designation: string;
  type: 'immobilier' | 'vehicule' | 'equipement' | 'stock' | 'autre';
  description?: string;
  
  // Valeurs financières détaillées
  prixAchat?: number; // Prix d'achat original
  valeurActuelle?: number; // Valeur actuelle estimée
  devise?: 'USD' | 'CDF' | 'EUR';
  
  // Informations temporelles
  dateAcquisition?: string; // Date d'acquisition
  
  // État et localisation
  etatActuel?: 'neuf' | 'excellent' | 'bon' | 'moyen' | 'mauvais' | 'deteriore';
  localisation?: string;
  
  // Informations techniques
  numeroSerie?: string;
  marque?: string;
  modele?: string;
  quantite?: number;
  unite?: string;
  
  // Statut de propriété
  proprietaire?: 'propre' | 'location' | 'leasing' | 'emprunt';
  
  // Observations
  observations?: string;
  
  // Champs hérités pour compatibilité
  valeur?: number; // Pour compatibilité descendante
  etatUsage?: 'neuf' | 'bon' | 'moyen' | 'mauvais'; // Pour compatibilité descendante
}

interface AssetInputProps {
  label: string;
  value: Asset[];
  onSave: (assets: Asset[]) => void;
  disabled?: boolean;
  placeholder?: string;
  assetType?: 'immobilier' | 'vehicule' | 'equipement' | 'stock' | 'tous';
  showFinancialDetails?: boolean;
}

export const AssetInput: React.FC<AssetInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Ajouter un actif",
  assetType = 'tous',
  showFinancialDetails = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [assets, setAssets] = useState<Asset[]>(value);
  const [newAsset, setNewAsset] = useState<Partial<Asset>>({
    type: assetType !== 'tous' ? assetType : 'equipement',
    devise: 'USD',
    etatActuel: 'bon',
    proprietaire: 'propre',
    quantite: 1,
    dateAcquisition: new Date().toISOString().split('T')[0] // Date par défaut = aujourd'hui
  });

  // ✨ SYNC: Synchroniser assets quand value change depuis le parent
  // IMPORTANT: S'assurer que les actifs ont le bon type pour ce composant
  useEffect(() => {
    if (!isEditing) {
      const correctedAssets = (value || []).map(asset => ({
        ...asset,
        type: assetType !== 'tous' ? assetType : (asset.type || 'equipement')
      }));
      setAssets(correctedAssets);
    }
  }, [value, isEditing, assetType]);

  const handleStartEditing = () => {
    setIsEditing(true);
    // Corriger automatiquement le type des actifs si assetType est spécifié
    // IMPORTANT: S'assurer que les actifs ont le bon type pour ce composant
    const correctedAssets = (value || []).map(asset => ({
      ...asset,
      type: assetType !== 'tous' ? assetType : (asset.type || 'equipement')
    }));
    setAssets(correctedAssets);
    // Reset aussi le nouvel actif avec le bon type
    setNewAsset({
      type: assetType !== 'tous' ? assetType : 'equipement',
      devise: 'USD',
      etatActuel: 'bon',
      proprietaire: 'propre',
      quantite: 1,
      dateAcquisition: new Date().toISOString().split('T')[0]
    });
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // IMPORTANT: Copie profonde pour éviter les mutations croisées
    const copiedAssets = (value || []).map(a => ({ ...a }));
    setAssets(copiedAssets);
    setNewAsset({
      type: assetType !== 'tous' ? assetType : 'equipement',
      devise: 'USD',
      etatActuel: 'bon',
      proprietaire: 'propre',
      quantite: 1,
      dateAcquisition: new Date().toISOString().split('T')[0]
    });
  };

  const handleSaveEditing = () => {
    // IMPORTANT: S'assurer que tous les actifs ont le bon type avant de sauvegarder
    const assetsToSave = assets.map(asset => ({
      ...asset,
      type: assetType !== 'tous' ? assetType : (asset.type || 'equipement')
    }));
    onSave(assetsToSave);
    setIsEditing(false);
    setNewAsset({
      type: assetType !== 'tous' ? assetType : 'equipement',
      devise: 'USD',
      etatActuel: 'bon',
      proprietaire: 'propre',
      quantite: 1,
      dateAcquisition: new Date().toISOString().split('T')[0]
    });
  };

  const handleAddAsset = () => {
    if (newAsset.designation && newAsset.type) {
      const asset: Asset = {
        id: Date.now().toString(),
        designation: newAsset.designation || '',
        type: newAsset.type || 'equipement',
        description: newAsset.description || '',
        prixAchat: newAsset.prixAchat || 0,
        valeurActuelle: newAsset.valeurActuelle || newAsset.prixAchat || 0,
        devise: newAsset.devise || 'USD',
        dateAcquisition: newAsset.dateAcquisition || new Date().toISOString().split('T')[0],
        etatActuel: newAsset.etatActuel || 'bon',
        localisation: newAsset.localisation || '',
        numeroSerie: newAsset.numeroSerie || '',
        marque: newAsset.marque || '',
        modele: newAsset.modele || '',
        quantite: newAsset.quantite || 1,
        unite: newAsset.unite || '',
        proprietaire: newAsset.proprietaire || 'propre',
        observations: newAsset.observations || '',
        // Champs de compatibilité
        valeur: newAsset.valeurActuelle || newAsset.prixAchat || 0,
        etatUsage: newAsset.etatActuel as any || 'bon'
      };
      setAssets([...assets, asset]);
      // Reset le formulaire pour un nouvel actif
      setNewAsset({
        type: assetType !== 'tous' ? assetType : 'equipement',
        devise: 'USD',
        etatActuel: 'bon',
        proprietaire: 'propre',
        quantite: 1,
        dateAcquisition: new Date().toISOString().split('T')[0]
      });
    }
  };

  const handleRemoveAsset = (index: number) => {
    const updatedAssets = assets.filter((_, i) => i !== index);
    setAssets(updatedAssets);
  };

  const handleEditAsset = (index: number, field: keyof Asset, value: any) => {
    const updatedAssets = [...assets];
    updatedAssets[index] = { ...updatedAssets[index], [field]: value };
    setAssets(updatedAssets);
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'immobilier': return <Building className="w-4 h-4" />;
      case 'vehicule': return <Car className="w-4 h-4" />;
      case 'equipement': return <Wrench className="w-4 h-4" />;
      case 'stock': return <Package className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getAssetTypeLabel = (type: string) => {
    switch (type) {
      case 'immobilier': return 'Immobilier';
      case 'vehicule': return 'Véhicule';
      case 'equipement': return 'Équipement';
      case 'stock': return 'Stock';
      default: return 'Autre';
    }
  };

  const getAssetTypes = () => {
    if (assetType !== 'tous') {
      return [{ value: assetType, label: getAssetTypeLabel(assetType) }];
    }
    return [
      { value: 'immobilier', label: 'Immobilier' },
      { value: 'vehicule', label: 'Véhicule' },
      { value: 'equipement', label: 'Équipement' },
      { value: 'stock', label: 'Stock' },
      { value: 'autre', label: 'Autre' }
    ];
  };

  const getEtatActuelColor = (etat: string) => {
    switch (etat) {
      case 'neuf': return 'bg-green-100 text-green-800';
      case 'excellent': return 'bg-emerald-100 text-emerald-800';
      case 'bon': return 'bg-blue-100 text-blue-800';
      case 'moyen': return 'bg-yellow-100 text-yellow-800';
      case 'mauvais': return 'bg-orange-100 text-orange-800';
      case 'deteriore': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEtatActuelLabel = (etat: string) => {
    switch (etat) {
      case 'neuf': return 'Neuf';
      case 'excellent': return 'Excellent';
      case 'bon': return 'Bon état';
      case 'moyen': return 'État moyen';
      case 'mauvais': return 'Mauvais état';
      case 'deteriore': return 'Détérioré';
      default: return 'Non spécifié';
    }
  };

  const getProprietaireColor = (proprietaire: string) => {
    switch (proprietaire) {
      case 'propre': return 'bg-green-100 text-green-800';
      case 'location': return 'bg-blue-100 text-blue-800';
      case 'leasing': return 'bg-purple-100 text-purple-800';
      case 'emprunt': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {!disabled && (
            <button
              onClick={handleStartEditing}
              className="text-[#197ca8] hover:text-[#156a92] text-sm flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Modifier
            </button>
          )}
        </div>
        
        {value.length === 0 ? (
          <div className="text-gray-500 text-sm italic p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
            {placeholder}
          </div>
        ) : (
          <div className="space-y-3">
            {value.map((asset) => (
              <div key={asset.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* En-tête de l'actif - responsive */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-[#197ca8] flex-shrink-0">
                        {getAssetIcon(asset.type)}
                      </span>
                      <span className="font-medium text-gray-900 break-words">
                        {asset.designation}
                      </span>
                      <span className="px-2 py-1 bg-[#197ca8] text-white text-xs rounded flex-shrink-0">
                        {getAssetTypeLabel(asset.type)}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {asset.description && (
                      <div className="text-gray-700 text-sm mb-3 break-words">
                        {asset.description}
                      </div>
                    )}
                    
                    {/* Grille d'informations responsive */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600">
                      {/* Badges État et Propriété */}
                      <div className="flex flex-row sm:flex-col gap-2 sm:gap-1">
                        <span className={`px-2 py-1 rounded text-xs text-center ${getEtatActuelColor(asset.etatActuel || asset.etatUsage || 'bon')}`}>
                          {getEtatActuelLabel(asset.etatActuel || asset.etatUsage || 'bon')}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs text-center ${getProprietaireColor(asset.proprietaire || 'propre')}`}>
                          {asset.proprietaire === 'propre' ? 'Propriété' : 
                           asset.proprietaire === 'location' ? 'Location' :
                           asset.proprietaire === 'leasing' ? 'Leasing' :
                           asset.proprietaire === 'emprunt' ? 'Emprunt' : 'Propriété'}
                        </span>
                      </div>

                      {/* Date d'acquisition */}
                      {asset.dateAcquisition && (
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-500 text-xs">Acquisition</span>
                          <span>{new Date(asset.dateAcquisition).toLocaleDateString('fr-FR')}</span>
                        </div>
                      )}

                      {/* Valeurs financières */}
                      {showFinancialDetails && (asset.prixAchat || asset.valeurActuelle) && (
                        <div className="flex flex-col space-y-1">
                          {asset.prixAchat && (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-500 text-xs">Prix d'achat</span>
                              <span>{asset.prixAchat.toLocaleString()} {asset.devise}</span>
                            </div>
                          )}
                          {asset.valeurActuelle && (
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-500 text-xs">Valeur actuelle</span>
                              <span className="text-[#197ca8] font-medium flex items-center">
                                <DollarSign className="w-3 h-3 mr-1 flex-shrink-0" />
                                {asset.valeurActuelle.toLocaleString()} {asset.devise}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Informations supplémentaires selon le type */}
                      <div className="flex flex-col space-y-1">
                        {/* Pour immobilier: superficie */}
                        {asset.type === 'immobilier' && asset.quantite && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-500 text-xs">Superficie</span>
                            <span>{asset.quantite} m²</span>
                          </div>
                        )}
                        
                        {/* Pour autres types: quantité si > 1 */}
                        {asset.type !== 'immobilier' && asset.quantite && asset.quantite > 1 && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-500 text-xs">Quantité</span>
                            <span>{asset.quantite} {asset.unite}</span>
                          </div>
                        )}

                        {/* Marque/Modèle pour véhicules et équipements */}
                        {(asset.type === 'vehicule' || asset.type === 'equipement') && asset.marque && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-500 text-xs">Marque/Modèle</span>
                            <span className="break-words">{asset.marque} {asset.modele}</span>
                          </div>
                        )}

                        {/* Localisation */}
                        {asset.localisation && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-500 text-xs">
                              {asset.type === 'immobilier' ? 'Adresse' : 'Localisation'}
                            </span>
                            <span className="break-words">{asset.localisation}</span>
                          </div>
                        )}
                        
                        {/* N° Immatriculation pour véhicules */}
                        {asset.type === 'vehicule' && asset.numeroSerie && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-500 text-xs">Immatriculation</span>
                            <span>{asset.numeroSerie}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCancelEditing}
            className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={handleSaveEditing}
            className="text-green-600 hover:text-green-800 text-sm flex items-center"
          >
            <Save className="w-4 h-4 mr-1" />
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Liste des actifs existants */}
      <div className="space-y-3">
        {assets.map((asset, index) => (
          <div key={asset.id} className="bg-white border-2 border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center min-w-0">
                <span className="text-[#197ca8] mr-2 flex-shrink-0">
                  {getAssetIcon(asset.type)}
                </span>
                <span className="font-medium text-gray-900 truncate">
                  Actif #{index + 1}
                </span>
              </div>
              <button
                onClick={() => handleRemoveAsset(index)}
                className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Désignation *</label>
                <input
                  type="text"
                  value={asset.designation}
                  onChange={(e) => handleEditAsset(index, 'designation', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={assetType !== 'tous' ? assetType : asset.type}
                  onChange={(e) => handleEditAsset(index, 'type', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                  disabled={assetType !== 'tous'}
                >
                  {getAssetTypes().map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">État actuel</label>
                <select
                  value={asset.etatActuel || asset.etatUsage || 'bon'}
                  onChange={(e) => handleEditAsset(index, 'etatActuel', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                >
                  <option value="neuf">Neuf</option>
                  <option value="excellent">Excellent</option>
                  <option value="bon">Bon état</option>
                  <option value="moyen">État moyen</option>
                  <option value="mauvais">Mauvais état</option>
                  <option value="deteriore">Détérioré</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date d'acquisition</label>
                <input
                  type="date"
                  value={asset.dateAcquisition || ''}
                  onChange={(e) => handleEditAsset(index, 'dateAcquisition', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>

              {showFinancialDetails && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Prix d'achat</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={asset.prixAchat || asset.valeur || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        handleEditAsset(index, 'prixAchat', value);
                        // Synchroniser avec valeur pour compatibilité
                        if (!asset.valeurActuelle) {
                          handleEditAsset(index, 'valeurActuelle', value);
                        }
                      }}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Valeur actuelle</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={asset.valeurActuelle || asset.valeur || 0}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        handleEditAsset(index, 'valeurActuelle', value);
                        // Synchroniser avec valeur pour compatibilité
                        handleEditAsset(index, 'valeur', value);
                      }}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Devise</label>
                    <select
                      value={asset.devise}
                      onChange={(e) => handleEditAsset(index, 'devise', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    >
                      <option value="USD">USD</option>
                      <option value="CDF">CDF</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </>
              )}

              {/* Champs conditionnels selon le type d'actif en mode édition */}
              {(asset.type === 'immobilier' || assetType === 'immobilier') ? (
                /* Champs spécifiques aux biens immobiliers */
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Adresse / Localisation</label>
                    <input
                      type="text"
                      value={asset.localisation || ''}
                      onChange={(e) => handleEditAsset(index, 'localisation', e.target.value)}
                      placeholder="Ex: Avenue du Commerce, n°25, Kinshasa"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Superficie (m²)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={asset.quantite || ''}
                      onChange={(e) => {
                        handleEditAsset(index, 'quantite', parseFloat(e.target.value) || 0);
                        handleEditAsset(index, 'unite', 'm²');
                      }}
                      placeholder="Ex: 250"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Statut de propriété</label>
                    <select
                      value={asset.proprietaire || 'propre'}
                      onChange={(e) => handleEditAsset(index, 'proprietaire', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    >
                      <option value="propre">Propriété</option>
                      <option value="location">Location</option>
                      <option value="leasing">Leasing</option>
                      <option value="emprunt">Emprunt / Hypothèque</option>
                    </select>
                  </div>
                </>
              ) : (asset.type === 'vehicule' || assetType === 'vehicule') ? (
                /* Champs spécifiques aux véhicules */
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marque</label>
                    <input
                      type="text"
                      value={asset.marque || ''}
                      onChange={(e) => handleEditAsset(index, 'marque', e.target.value)}
                      placeholder="Ex: Toyota, Mercedes..."
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Modèle</label>
                    <input
                      type="text"
                      value={asset.modele || ''}
                      onChange={(e) => handleEditAsset(index, 'modele', e.target.value)}
                      placeholder="Ex: Hilux, Sprinter..."
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">N° Immatriculation</label>
                    <input
                      type="text"
                      value={asset.numeroSerie || ''}
                      onChange={(e) => handleEditAsset(index, 'numeroSerie', e.target.value)}
                      placeholder="Ex: ABC 1234 CD"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Statut de propriété</label>
                    <select
                      value={asset.proprietaire || 'propre'}
                      onChange={(e) => handleEditAsset(index, 'proprietaire', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    >
                      <option value="propre">Propriété</option>
                      <option value="location">Location</option>
                      <option value="leasing">Leasing</option>
                      <option value="emprunt">Emprunt</option>
                    </select>
                  </div>
                </>
              ) : (
                /* Champs par défaut pour équipement, stock, autre */
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Propriétaire</label>
                    <select
                      value={asset.proprietaire}
                      onChange={(e) => handleEditAsset(index, 'proprietaire', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    >
                      <option value="propre">Propriété</option>
                      <option value="location">Location</option>
                      <option value="leasing">Leasing</option>
                      <option value="emprunt">Emprunt</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantité</label>
                    <input
                      type="number"
                      min="1"
                      value={asset.quantite}
                      onChange={(e) => handleEditAsset(index, 'quantite', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Unité</label>
                    <input
                      type="text"
                      value={asset.unite}
                      onChange={(e) => handleEditAsset(index, 'unite', e.target.value)}
                      placeholder="ex: pièces, kg, m²"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marque</label>
                    <input
                      type="text"
                      value={asset.marque || ''}
                      onChange={(e) => handleEditAsset(index, 'marque', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Modèle</label>
                    <input
                      type="text"
                      value={asset.modele || ''}
                      onChange={(e) => handleEditAsset(index, 'modele', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Localisation</label>
                    <input
                      type="text"
                      value={asset.localisation || ''}
                      onChange={(e) => handleEditAsset(index, 'localisation', e.target.value)}
                      placeholder="Ex: Bureau principal, Entrepôt A..."
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={asset.description}
                onChange={(e) => handleEditAsset(index, 'description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Ajouter un nouvel actif */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
        <h4 className="font-medium text-blue-900 mb-3 text-sm sm:text-base">
          Ajouter un nouvel actif
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Désignation *</label>
            <input
              type="text"
              value={newAsset.designation || ''}
              onChange={(e) => setNewAsset({...newAsset, designation: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
            <select
              value={newAsset.type || (assetType !== 'tous' ? assetType : 'equipement')}
              onChange={(e) => setNewAsset({...newAsset, type: e.target.value as any})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              disabled={assetType !== 'tous'}
            >
              {getAssetTypes().map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date d'acquisition</label>
            <input
              type="date"
              value={newAsset.dateAcquisition || ''}
              onChange={(e) => setNewAsset({...newAsset, dateAcquisition: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">État actuel</label>
            <select
              value={newAsset.etatActuel || 'bon'}
              onChange={(e) => setNewAsset({...newAsset, etatActuel: e.target.value as any})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            >
              <option value="neuf">Neuf</option>
              <option value="excellent">Excellent</option>
              <option value="bon">Bon état</option>
              <option value="moyen">État moyen</option>
              <option value="mauvais">Mauvais état</option>
              <option value="deteriore">Détérioré</option>
            </select>
          </div>
        </div>

        {showFinancialDetails && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prix d'achat</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newAsset.prixAchat || ''}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setNewAsset({
                    ...newAsset, 
                    prixAchat: value,
                    valeurActuelle: newAsset.valeurActuelle || value // Auto-remplir si pas définie
                  });
                }}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Valeur actuelle</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newAsset.valeurActuelle || ''}
                onChange={(e) => setNewAsset({...newAsset, valeurActuelle: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Devise</label>
              <select
                value={newAsset.devise || 'USD'}
                onChange={(e) => setNewAsset({...newAsset, devise: e.target.value as any})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              >
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>
        )}

        {/* Champs conditionnels selon le type d'actif */}
        {(newAsset.type === 'immobilier' || (assetType === 'immobilier' && assetType !== 'tous')) ? (
          /* Champs spécifiques aux biens immobiliers */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Adresse / Localisation</label>
              <input
                type="text"
                value={newAsset.localisation || ''}
                onChange={(e) => setNewAsset({...newAsset, localisation: e.target.value})}
                placeholder="Ex: Avenue du Commerce, n°25, Kinshasa"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Superficie (m²)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newAsset.quantite || ''}
                onChange={(e) => setNewAsset({...newAsset, quantite: parseFloat(e.target.value) || 0, unite: 'm²'})}
                placeholder="Ex: 250"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Statut de propriété</label>
              <select
                value={newAsset.proprietaire || 'propre'}
                onChange={(e) => setNewAsset({...newAsset, proprietaire: e.target.value as any})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              >
                <option value="propre">Propriété</option>
                <option value="location">Location</option>
                <option value="leasing">Leasing</option>
                <option value="emprunt">Emprunt / Hypothèque</option>
              </select>
            </div>
          </div>
        ) : (newAsset.type === 'vehicule' || assetType === 'vehicule') ? (
          /* Champs spécifiques aux véhicules */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3 sm:mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Marque</label>
              <input
                type="text"
                value={newAsset.marque || ''}
                onChange={(e) => setNewAsset({...newAsset, marque: e.target.value})}
                placeholder="Ex: Toyota, Mercedes..."
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Modèle</label>
              <input
                type="text"
                value={newAsset.modele || ''}
                onChange={(e) => setNewAsset({...newAsset, modele: e.target.value})}
                placeholder="Ex: Hilux, Sprinter..."
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">N° Immatriculation</label>
              <input
                type="text"
                value={newAsset.numeroSerie || ''}
                onChange={(e) => setNewAsset({...newAsset, numeroSerie: e.target.value})}
                placeholder="Ex: ABC 1234 CD"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Statut de propriété</label>
              <select
                value={newAsset.proprietaire || 'propre'}
                onChange={(e) => setNewAsset({...newAsset, proprietaire: e.target.value as any})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              >
                <option value="propre">Propriété</option>
                <option value="location">Location</option>
                <option value="leasing">Leasing</option>
                <option value="emprunt">Emprunt</option>
              </select>
            </div>
          </div>
        ) : (
          /* Champs par défaut pour équipement, stock, autre */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Marque</label>
              <input
                type="text"
                value={newAsset.marque || ''}
                onChange={(e) => setNewAsset({...newAsset, marque: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Modèle</label>
              <input
                type="text"
                value={newAsset.modele || ''}
                onChange={(e) => setNewAsset({...newAsset, modele: e.target.value})}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Localisation</label>
              <input
                type="text"
                value={newAsset.localisation || ''}
                onChange={(e) => setNewAsset({...newAsset, localisation: e.target.value})}
                placeholder="Ex: Bureau principal, Entrepôt A..."
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>
          </div>
        )}

        <div className="mt-3 sm:mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={newAsset.description || ''}
            onChange={(e) => setNewAsset({...newAsset, description: e.target.value})}
            rows={2}
            placeholder="Description détaillée de l'actif, spécifications techniques..."
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
          />
        </div>

        <div className="mt-3 sm:mt-4 flex justify-end">
          <button
            onClick={handleAddAsset}
            disabled={!newAsset.designation || !newAsset.type}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};